/**
 * Shared display helpers so money, statuses and codes read the same on every
 * screen. Prefer these over ad-hoc `₱${value}` or rendering raw enum values.
 */

const pesoFormatter = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "₱12,500.00". Returns "—" for null/blank/non-numeric input. */
export function formatPeso(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return pesoFormatter.format(n);
}

/** `ready_to_bill` → "Ready to bill"; `on-trip` → "On trip". */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  const spaced = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Vehicle run label for the board / trip detail: "Run 1 · Closed", "Run 2 · Active".
 * A truck may run more than once a day; older API rows without a number read as Run 1.
 */
export function vehicleRunLabel(run: { runNumber?: number | null; status?: string | null }): string {
  const n = run.runNumber && run.runNumber > 0 ? run.runNumber : 1;
  const status = humanize(run.status);
  return status ? `Run ${n} · ${status}` : `Run ${n}`;
}

const STOP_TYPE_LABELS: Record<string, string> = {
  pickup: 'Pickup',
  drop: 'Delivery',
  return_to_base: 'Return to base',
};

export function stopTypeLabel(type: string | null | undefined): string {
  if (!type) return '';
  return STOP_TYPE_LABELS[type] ?? humanize(type);
}

/** Client-facing trip status wording (portal). Internal billing states read as "Delivered". */
const CLIENT_STATUS_LABELS: Record<string, string> = {
  requested: 'Awaiting confirmation',
  confirmed: 'Confirmed',
  dispatched: 'Truck assigned',
  executing: 'In transit',
  completed: 'Delivered',
  ready_to_bill: 'Delivered',
  billed: 'Delivered',
  closed: 'Delivered',
  on_hold: 'On hold',
  declined: 'Declined',
  cancelled: 'Cancelled',
  failed: 'Delivery failed',
  quote_required: 'Quote in progress',
};

export function clientStatusLabel(status: string): string {
  return CLIENT_STATUS_LABELS[status] ?? humanize(status);
}

/** "Oct 26, 2026, 2:14 PM" in Manila time. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-PH', { timeZone: 'Asia/Manila', dateStyle: 'medium', timeStyle: 'short' });
}

/** "Oct 26, 2026" in Manila time. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value.length === 10 ? `${value}T00:00:00+08:00` : value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-PH', { timeZone: 'Asia/Manila', dateStyle: 'medium' });
}

/** "just now", "2 min ago", "3 h ago", "2 days ago" from an age in seconds. */
export function formatAgo(ageSeconds: number | null | undefined): string {
  if (ageSeconds == null || !Number.isFinite(ageSeconds)) return '—';
  const s = Math.max(0, Math.round(ageSeconds));
  if (s < 45) return 'just now';
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min ago`;
  const h = Math.round(s / 3600);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(s / 86400);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** Age in seconds of a timestamp relative to `now`. */
export function secondsSince(value: string | Date | null | undefined, now: number = Date.now()): number | null {
  if (!value) return null;
  const t = typeof value === 'string' ? Date.parse(value) : value.getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, (now - t) / 1000);
}

/** "48.2 MB" for a byte count. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}
